import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { fetchWordPressBookById } from "@/lib/wordpress";
import { Book } from "@/types/book";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Download, BookOpen, Star, Calendar, ArrowLeft, FileText, Volume2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const BookDetail = () => {
  const { id } = useParams();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBook = async () => {
      if (!id) return;
      setLoading(true);
      const fetchedBook = await fetchWordPressBookById(Number(id));
      setBook(fetchedBook);
      setLoading(false);
    };
    loadBook();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Loading book details...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-bold mb-4">Book Not Found</h1>
          <Link to="/library">
            <Button>Back to Library</Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const handleReadOnline = () => {
    if (book?.pdfUrl || book?.txtUrl || book?.audioUrl) {
      // Determine the best format to start with
      let format = 'pdf';
      if (!book.pdfUrl && book.txtUrl) {
        format = 'txt';
      } else if (!book.pdfUrl && !book.txtUrl && book.audioUrl) {
        format = 'audio';
      }
      
      // Navigate to integrated reader
      window.location.href = `/reader/${book.id}?format=${format}`;
    } else {
      toast.error("No readable formats available for this book.");
    }
  };

  const handleDownload = () => {
    if (book?.pdfUrl) {
      const link = document.createElement('a');
      link.href = book.pdfUrl;
      link.download = `${book.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started!");
    } else {
      toast.error("PDF file not available for this book.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12">
        <Link to="/library">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Library
          </Button>
        </Link>

        <div className="grid md:grid-cols-2 gap-12 mb-12">
          <div className="animate-scale-in">
            <Card className="overflow-hidden border-0 gradient-card">
              <img
                src={book.coverImage}
                alt={`${book.title} cover`}
                className="w-full aspect-[2/3] object-cover"
              />
            </Card>
          </div>

          <div className="space-y-6 animate-fade-in">
            <div>
              <Badge className="mb-3">{book.category}</Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-3">{book.title}</h1>
              <p className="text-xl text-muted-foreground mb-4">by {book.author}</p>
              
              <div className="flex gap-6 text-sm text-muted-foreground mb-6">
                {book.rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-5 w-5 fill-primary text-primary" />
                    <span className="font-medium">{book.rating.toFixed(1)}</span>
                  </div>
                )}
                {book.downloads && (
                  <div className="flex items-center gap-1">
                    <Download className="h-5 w-5" />
                    <span>{book.downloads} downloads</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="h-5 w-5" />
                  <span>{new Date(book.publishDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button 
                size="lg" 
                className="flex-1" 
                onClick={handleReadOnline}
                disabled={!book?.pdfUrl && !book?.txtUrl && !book?.audioUrl}
              >
                <BookOpen className="mr-2 h-5 w-5" />
                Read Online
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="flex-1" 
                onClick={handleDownload}
                disabled={!book?.pdfUrl}
              >
                <Download className="mr-2 h-5 w-5" />
                Download PDF
              </Button>
            </div>

            {/* Available Formats Info */}
            <div className="flex flex-wrap gap-2 mt-4">
              {book?.pdfUrl && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  PDF Available
                </Badge>
              )}
              {book?.txtUrl && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Text Available
                </Badge>
              )}
              {book?.audioUrl && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Volume2 className="h-3 w-3" />
                  Audio Available
                </Badge>
              )}
            </div>

            <Card className="gradient-card">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-3">About This Book</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {book.description}
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookDetail;
